import React from 'react'
import EnhancedMeta from '../components/meta/enhanced-meta'
import Layout from '../components/layout/layout'
import { graphql } from 'gatsby'
import Parallax from '../components/parallax'
import ServiceTable from '../components/service-table'
import GoogleMapsEmbed from '../components/google-maps-embed'
import { Treatment, Question } from '../interfeces'
import { generateServiceSchema, generateFAQSchema, generateBreadcrumbSchema } from '../utils/schema-generator'
import RelatedTreatments from '../components/related-treatments'

interface Props {
  data: any
  location: Location
}

const treatment: Treatment = {
  duration: '15-30 minutes',
  session: '1 session',
  results: 'Results 3-6 months',
  cost: 'From £175',
  downtime: 'Minimal to none',
}

const relatedTreatments = [
  {
    name: 'Botox in Cheshunt',
    slug: 'botox-cheshunt',
    description:
      'Explore our dedicated Botox page for in-depth information on anti-wrinkle injections in Cheshunt.',
  },
  {
    name: 'Dermal Fillers Cheshunt',
    slug: 'dermal-fillers-cheshunt',
    description:
      'Combine dermal fillers with Botox to restore facial volume and smooth deeper lines.',
  },
  {
    name: 'Lip Fillers Cheshunt',
    slug: 'lip-fillers',
    description:
      'Enhance lip volume and definition to complement your smoother, younger-looking skin.',
  },
]

const questions: Array<Question> = [
  {
    question: 'How much does Botox cost in Cheshunt?',
    answer:
      'Botox treatments at Ada Aesthetics start from £175 for one area, depending on the number of areas treated. We offer competitive pricing for the Hertfordshire area. Book a free consultation to receive a personalized quote based on your specific treatment areas.',
  },
  {
    question: 'How long does Botox last?',
    answer:
      'Botox results typically last between 3 to 6 months. We recommend follow-up appointments to maintain your results and ensure optimal anti-wrinkle treatment outcomes.',
  },
  {
    question: 'Is Botox safe?',
    answer:
      'Yes, when administered by qualified professionals like our experienced practitioners at Ada Aesthetics. Botox has been used safely for decades and is FDA-approved for cosmetic use. We follow strict safety protocols.',
  },
  {
    question: 'What areas can be treated with Botox?',
    answer:
      'At Ada Aesthetics, we treat forehead lines, frown lines between the eyebrows, crow\'s feet around the eyes, bunny lines on the nose, and other facial areas. Our practitioners will assess your needs during consultation.',
  },
  {
    question: 'Does Botox hurt?',
    answer:
      'Most clients report minimal discomfort. We use fine needles and can apply numbing cream if needed. The procedure is quick, and any discomfort is brief.',
  },
  {
    question: 'How soon will I see Botox results?',
    answer:
      'You\'ll typically notice results within 3-5 days, with full effects visible after 10-14 days. We schedule a follow-up to ensure you\'re happy with your results.',
  },
  {
    question: 'Can I go back to work after Botox?',
    answer:
      'Yes! Botox is often called a "lunchtime treatment" because there\'s no downtime. Clients typically return to work or daily activities immediately after their appointment.',
  },
  {
    question: 'Who shouldn\'t have Botox?',
    answer:
      'Botox isn\'t recommended if you\'re pregnant, breastfeeding, or have certain neurological conditions. During your consultation, we\'ll review your medical history to ensure Botox is safe for you.',
  },
]

const AntiWrinkleInjectionsCheshunt: React.FC<Props> = ({ data, location }: Props) => {
  const meta = data.site?.meta

  const serviceSchema = generateServiceSchema(
    'Botox Anti-Wrinkle Injections in Cheshunt',
    'Professional Botox treatments in Cheshunt, Hertfordshire. Expert anti-wrinkle injections to reduce fine lines and wrinkles. Qualified practitioners serving Cheshunt, Waltham Cross, and surrounding areas.',
    '175'
  )

  const faqSchema = generateFAQSchema(questions.map(q => ({
    question: q.question,
    answer: q.answer
  })))

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://adaaesthetics.uk' },
    { name: 'Treatments', url: 'https://adaaesthetics.uk/treatments' },
    { name: 'Anti-Wrinkle Injections Cheshunt', url: 'https://adaaesthetics.uk/anti-wrinkle-injections-cheshunt' }
  ])

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [serviceSchema, faqSchema, breadcrumbSchema]
  }

  return (
    <Layout location={location}>
      <EnhancedMeta
        title="Anti-Wrinkle Injections Cheshunt | Botox Treatments | Ada Aesthetics"
        description="Professional anti-wrinkle injections in Cheshunt from £175. Reduce wrinkles & fine lines with qualified practitioners. Serving Cheshunt, Waltham Cross & surrounding areas. Book today!"
        keywords="anti-wrinkle injections Cheshunt, Botox Cheshunt, Botox Hertfordshire, wrinkle treatment Cheshunt, facial aesthetics Cheshunt"
        url="https://adaaesthetics.uk/anti-wrinkle-injections-cheshunt"
        canonicalUrl="https://adaaesthetics.uk/botox-cheshunt"
        schemaData={combinedSchema}
        site={meta}
      />
      <Parallax
        titleName="Botox in Cheshunt"
        customClass="page_title"
      />

      <section className="ls service-single s-pt-70 s-pb-20 s-pb-sm-50 s-py-lg-100 s-pt-xl-150 s-pb-xl-100 c-gutter-60 c-mb-50">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h1 className="special-heading">Erase Wrinkles & Smooth Fine Lines with Expert Botox in Cheshunt</h1>
              <p className="excerpt">
                Tired of seeing those worry lines every time you look in the mirror? Anti-wrinkle injections at Ada Aesthetics in Cheshunt smooth away years of expression lines in just one quick lunchtime treatment. Our qualified practitioners deliver natural-looking results that help you look refreshed and confident—not frozen. From £175. Proudly serving Waltham Cross, Broxbourne, and surrounding Hertfordshire areas.
              </p>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <picture>
                <source srcSet="/images/blog/08.webp" type="image/webp" />
                <source srcSet="/images/blog/08.jpg" type="image/jpeg" />
                <img
                  src="/images/blog/08.jpg"
                  className="alignright"
                  alt="Botox anti-wrinkle treatment at Ada Aesthetics Cheshunt"
                />
              </picture>

              <h2>What is Botox?</h2>
              <p>
                Botox (Botulinum Toxin Type A) is a safe, FDA-approved treatment used to reduce the appearance of fine lines
                and wrinkles. We use Botox to relax facial muscles that cause dynamic wrinkles – those
                lines that appear when you smile, frown, or squint.
              </p>
              <p>
                Unlike <a href="/dermal-fillers-cheshunt">dermal fillers</a> that add volume, Botox works by temporarily blocking nerve signals to specific muscles.
                This allows your facial muscles to relax, smoothing existing wrinkles and preventing new ones from forming.
                It's the ultimate "prejuvenation" treatment for maintaining a youthful appearance.
                Many clients combine Botox with <a href="/lip-fillers">lip fillers</a> or <a href="/cheek-fillers">cheek fillers</a> for comprehensive facial rejuvenation.
              </p>

              <h2>Areas We Treat with Botox</h2>
              <p>Our experienced practitioners can treat multiple areas with Botox, including:</p>
              <ul>
                <li><strong>Forehead lines</strong> – horizontal lines across the forehead</li>
                <li><strong>Frown lines (glabellar lines)</strong> – vertical lines between the eyebrows</li>
                <li><strong>Crow's feet</strong> – lines around the outer corners of the eyes</li>
                <li><strong>Bunny lines</strong> – wrinkles on the bridge of the nose</li>
                <li><strong>Smoker's lines</strong> – vertical lines around the lips (even if you don't smoke)</li>
                <li><strong>Chin dimpling</strong> – textured appearance on the chin</li>
                <li><strong>Neck bands</strong> – vertical bands on the neck</li>
              </ul>

              <h2>Why Choose Ada Aesthetics for Botox in Cheshunt?</h2>
              <p>
                When choosing a Botox provider in Cheshunt, qualifications and experience matter. At Ada Aesthetics, you can trust that:
              </p>
              <ul>
                <li>All treatments are performed by <strong>qualified, experienced practitioners</strong></li>
                <li>We use only <strong>genuine, premium Botox products</strong></li>
                <li>We take time for thorough consultations to understand your goals</li>
                <li>Our approach focuses on <strong>natural-looking results</strong>, not frozen faces</li>
                <li>We're conveniently located in <strong>Cheshunt, Hertfordshire</strong></li>
                <li>We serve the local community including Waltham Cross, Broxbourne, and Hoddesdon</li>
              </ul>

              <h2>Your Botox Treatment Journey</h2>
              <h3>1. Consultation</h3>
              <p>
                Your journey begins with a comprehensive consultation. We'll discuss your concerns,
                assess your facial anatomy, review your medical history, and create a personalized treatment plan.
              </p>

              <h3>2. The Treatment</h3>
              <p>
                The actual Botox procedure takes just 15-30 minutes. We use ultra-fine needles to precisely inject small amounts
                of Botox into the targeted muscles. Most clients report minimal discomfort.
              </p>

              <h3>3. Aftercare</h3>
              <p>
                After your treatment, you can return to normal activities immediately. We'll provide specific aftercare instructions
                to ensure optimal results. Avoid lying down for 4 hours and strenuous exercise for 24 hours.
              </p>

              <h3>4. Results & Follow-up</h3>
              <p>
                You'll notice initial results within 3-5 days, with full effects visible after 10-14 days. Results typically last
                3-6 months. We schedule a follow-up appointment to assess your results and discuss maintenance.
              </p>

              <h2>Serving Your Local Area</h2>
              <p>
                Ada Aesthetics is proud to be your local Botox provider. Conveniently located at 15 Ousden Drive,
                Cheshunt, EN8 9RL, we're easily accessible for residents throughout Hertfordshire, including:
              </p>
              <ul>
                <li>Cheshunt</li>
                <li>Waltham Cross</li>
                <li>Broxbourne</li>
                <li>Hoddesdon</li>
                <li>Waltham Abbey</li>
                <li>Enfield</li>
              </ul>
              <p>
                No need to travel to London for quality Botox treatments – expert aesthetic care is available right here.
              </p>

              <h2>Who is Suitable for Botox?</h2>
              <p>
                Botox is suitable for adults aged 18+ who want to reduce the appearance of wrinkles or prevent them from deepening.
                We commonly treat clients in their 30s, 40s, 50s, and beyond.
              </p>
              <p>
                Botox is particularly effective for people who are:
              </p>
              <ul>
                <li>Noticing early signs of ageing and want to prevent wrinkles from deepening</li>
                <li>Looking to soften existing moderate to severe wrinkles</li>
                <li>Wanting a non-surgical, minimally invasive treatment</li>
                <li>Seeking natural-looking, refreshed results</li>
              </ul>

              <h2>Book Your Botox Consultation Today</h2>
              <p>
                Ready to refresh your appearance with expert Botox treatments? Join 500+ satisfied clients who trust us
                for natural-looking results. Schedule your consultation today - our friendly team is here to answer your questions
                and help you achieve your aesthetic goals.
              </p>
              <p>
                <strong>Limited availability this month</strong> - Call us today at <strong>07478705252</strong> or book online to secure your appointment.
                Your complimentary consultation (worth £50) includes facial assessment and personalized treatment plan.
              </p>

              <RelatedTreatments treatments={relatedTreatments} />

              <GoogleMapsEmbed
                embedUrl="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2469.9642841486544!2d-0.04449842346989817!3d51.70251797183743!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48761f5e6c8f7d0d%3A0x9f3e8f3e8f3e8f3e!2s15%20Ousden%20Drive%2C%20Cheshunt%2C%20Waltham%20Cross%20EN8%209RL!5e0!3m2!1sen!2suk!4v1234567890123"
                height={450}
                title="Ada Aesthetics Anti-Wrinkle Injections Clinic Location in Cheshunt"
                loading="lazy"
              />
            </div>
          </div>

          <ServiceTable treatment={treatment} questions={questions} />
        </div>
      </section>
    </Layout>
  )
}

export default AntiWrinkleInjectionsCheshunt

export const pageQuery = graphql`
  query AntiWrinkleInjectionsCheshunt {
    site {
      meta: siteMetadata {
        title
        description
        siteUrl
        author
      }
    }
  }
`
